// AUTO-GENERATED SEED — review before trusting. Rename keys (index→list/detail),
// refine params, confirm caller flags, add opt-in response types.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
// Type-only import (erased at build) — keeps the registry client-bundle-safe.
import type { StaffNotificationPreferencesDto } from '../../schemas/responses/staff-notification-preferences';

// BL-1833 PR 2b — a write about ANOTHER staff member, made in a tenant the
// caller states. The gate checks the caller's membership + grant there, then
// that the target (`userId`) is a live member of that same tenant (404 else).
const ACTING_ON_MEMBER = {
  from: 'shared_membership',
  tenantField: 'tenantId',
  memberField: 'userId',
  in: 'body',
} as const;

/** The caller's own preferences — nobody else's, so no grant applies. */
const SELF = { none: "self — the caller's own preference, keyed on their session" } as const;

export const user = {
  active_tenant: defineAdminRoute({
      path: '/api/user/active-tenant',
      params: {},
      methods: {
        PUT: { grant: SELF, tenant: { from: 'input', field: 'tenantId', in: 'body' } },
      },
    }),
  // Removes ONE tenant membership (BUG-990) — revoking the member's roles there.
  delete: defineAdminRoute({
      path: '/api/user/delete',
      params: {},
      methods: { POST: { grant: 'staff.revoke_role', tenant: ACTING_ON_MEMBER } },
    }),
  // BL-1832 proof route — the last of the six that gated on tenant alone: any
  // member of a tenant could list its whole staff roster, emails and phones
  // included. The ticket assigned it `shared_membership`, but it intersects no
  // second membership: the tenant is caller-stated (`?tenantId=`), which is
  // `input`. With none stated it lists super admins for a super admin — how
  // SuperAdminsSettings calls it — so the tenant is `optional`, and only a super
  // admin gets through tenant-less. Decided Michael Lawrie, 2026-09-22.
  list: defineAdminRoute({
      path: '/api/user/list',
      params: {},
      methods: {
        GET: {
          grant: 'staff.read',
          tenant: { from: 'input', field: 'tenantId', in: 'query', optional: true },
        },
      },
    }),
  // BL-1230 — the signed-in staff user's own opt-in-able notification toggles at
  // their ACTIVE tenant. Both verbs return the same eligibility-filtered shape, so
  // a save re-renders from the response without a follow-up GET.
  notification_preferences: defineAdminRoute({
      path: '/api/user/notification-preferences',
      params: {},
      methods: {
        GET: {
          grant: SELF,
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<StaffNotificationPreferencesDto>(),
        },
        PUT: {
          grant: SELF,
          tenant: { from: 'input', field: 'tenantId', in: 'query' },
          response: responseType<StaffNotificationPreferencesDto>(),
        },
      },
    }),
  profile: defineAdminRoute({
      path: '/api/user/profile',
      params: {},
      methods: {
        GET: { grant: SELF, tenant: { from: 'none', reason: "self — a staff profile belongs to no one tenant" } },
        PATCH: { grant: SELF, tenant: { from: 'none', reason: "self — a staff profile belongs to no one tenant" } },
      },
    }),
  tenant_roles: defineAdminRoute({
      path: '/api/user/tenant-roles',
      params: {},
      methods: { PATCH: { grant: 'staff.grant_role', tenant: ACTING_ON_MEMBER } },
    }),
  // The profile fields are global to the staff_users row; the tenant is the one
  // the Admin is editing FROM, which the target must belong to.
  update: defineAdminRoute({
      path: '/api/user/update',
      params: {},
      methods: { POST: { grant: 'staff.update', tenant: ACTING_ON_MEMBER } },
    }),
};
